import { TLeadStatus } from "@/models/leads.model";
import leadsService from "@/services/leads.service";
import { TRequestStatus } from "@models/msc.model";
import { Dispatch, PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "..";

type LeadsState = {
  initialized: boolean;
  status: TRequestStatus;
  list: TLeadStatus[];
};

//-----THUNK FUNCTIONS-----

const initialState: LeadsState = {
  initialized: false,
  status: "IDLE",
  list: [],
};

const leadStatusListSlice = createSlice({
  name: "leadStatusList",
  initialState,
  reducers: {
    startLoading(state) {
      state.status = "PENDING";
    },
    gotStatusList(state, action: PayloadAction<TLeadStatus[]>) {
      state.initialized = true;
      state.status = "IDLE";
      state.list = action.payload;
    },
    savedLeadStatus(state, action: PayloadAction<TLeadStatus>) {
      state.list.push(action.payload);
    },
    updatedLeadStatus(state, action: PayloadAction<TLeadStatus>) {
      const index = state.list.findIndex((status) => status.id === action.payload.id);
      if (index !== -1) state.list[index] = action.payload;
    },
    leadStatusRemoved(state, action: PayloadAction<number>) {
      state.list = state.list.filter((status) => status.id !== action.payload);
    },
    totalLeadsUpdated(state, action: PayloadAction<{ oldStatus: number; newStatus: number }>) {
      state.list = state.list.map((item) => {
        if (item.id === action.payload.oldStatus) return { ...item, totalLeads: item.totalLeads - 1 };
        if (item.id === action.payload.newStatus) return { ...item, totalLeads: item.totalLeads + 1 };
        return item;
      });
    },
  },
});

// ----------------------------

export const getLeadStatusList = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const { leadStatusList } = getState();
  const { list, initialized } = leadStatusList;

  // if already cached, return directly
  if (initialized) return list;

  dispatch(leadStatusListSlice.actions.startLoading());

  const response = await leadsService.getLeadStatusList();
  dispatch(leadStatusListSlice.actions.gotStatusList(response));
  return response;
};

// ----------------------------

export const saveLeadStatus = (data: Pick<TLeadStatus, "name" | "color">) => async (dispatch: Dispatch) => {
  const response = await leadsService.saveLeadStatus(data);
  dispatch(leadStatusListSlice.actions.savedLeadStatus(response));
  return response;
};

export const updateLeadStatus = (data: TLeadStatus) => async (dispatch: Dispatch) => {
  const response = await leadsService.updateLeadStatus(data);
  dispatch(leadStatusListSlice.actions.updatedLeadStatus(response));
  return response;
};

export const deleteLeadStatus = (id: number, newStatusId?: number) => async (dispatch: Dispatch) => {
  await leadsService.deleteLeadStatus(id, newStatusId);
  dispatch(leadStatusListSlice.actions.leadStatusRemoved(id));
  // update all leads with the newstatus
};

export const updateTotalLeads = (data: { oldStatus: number; newStatus: number }) => (dispatch: Dispatch) => {
  dispatch(leadStatusListSlice.actions.totalLeadsUpdated(data));
};

export default leadStatusListSlice.reducer;
